import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { KeyValue } from 'app/core/utils/keyValue';
import * as _ from 'lodash';
import { SachverhaltField, SachverhaltPrimitiveValue, SachverhaltSet } from '../../../../models/dossier/dossier-models';
import { SharedFeedbackService } from 'app/modules/feedback/services/shared-feedback.service';
import { I18nService } from 'app/core/services/i18n/i18n.service';
import { ApiClient, CreateFeedbackInputKategorie, ObjektAutoCompleteResult, ObjektTyp } from 'app/core/services/api-client';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, Subscription } from 'rxjs';
import { PermissionHelper } from 'app/core/utils/permission.helper';
import { ApiErrorHandlerService } from 'app/core/services/api-default-error-handler/api-error-handler.service';
import { takeUntil } from 'rxjs/operators';
import { abortSignalToObservable } from 'app/core/utils/abort-signal.utils';

@Component({
  selector: 'app-dynamic-control',
  templateUrl: './dynamic-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicControlComponent implements OnInit, OnDestroy {
  @Input()
  public optionValues: SachverhaltSet;

  @Input()
  public field: SachverhaltField;

  @Input()
  public value: SachverhaltPrimitiveValue;

  @Input()
  public maximizeWidth = true;

  @Input()
  public controlId: string;

  @Output()
  public rangeChanged = new EventEmitter<{ value: KeyValue[]; fieldKey: string }>();

  @Output()
  public textValueChange = new EventEmitter<{ value: string; fieldKey: string }>();

  @Output()
  public intValueChange = new EventEmitter<{ value: number; fieldKey: string }>();

  @Output()
  public doubleValueChange = new EventEmitter<{ value: number; fieldKey: string }>();

  @Output()
  public dateChange = new EventEmitter<{ value: Date; fieldKey: string }>();

  @Output()
  public dropdownValueChange = new EventEmitter<{ value: any; fieldKey: string }>();

  @Output()
  public fileLinkChanged = new EventEmitter<{ value: any; fieldKey: string }>();

  public userHasRight = false;
  private requiredUserRight = 710;
  private userDataSubscr: Subscription;

  constructor(
    private sharedFeedbackService: SharedFeedbackService,
    private i18n: I18nService,
    private securityService: OidcSecurityService,
    private apiClient: ApiClient
  ) {}

  ngOnInit() {
    this.userDataSubscr = this.securityService.userData$.subscribe((userData: any) => {
      // Handle the nested structure - actual user data is in userData.userData
      const actualUserData = userData?.userData;
      
      this.userHasRight = PermissionHelper.hasRight(actualUserData, this.requiredUserRight);
    });
  }

  ngOnDestroy(): void {
    this.userDataSubscr.unsubscribe();
  }

  public StringAsDate(value: string): Date {
    if (_.isNull(value) || _.isUndefined(value)) {
      return null;
    }
    return new Date(value);
  }

  public validateNumber(event: any) {
    const numRegex = new RegExp('^[0-9]*$');
    const maxValue = 2147483647; // intMax
    if (numRegex.test(event.target.value) === false) {
      event.target.value = event.target.value.replace(/\D/g, '');
    }
    if (parseInt(event.target.value, 10) > maxValue) {
      event.target.value = maxValue.toString();
    }
  }

  public intValueChangeHandler(value: number, field: string) {
    this.intValueChange.emit({ value: value, fieldKey: field });
  }

  openFeedbackWindow(table: string, value: string) {
    const description = this.i18n
      .getLocalizedString('components.lookup.feedback.description')
      .replace('{{table}}', table);
    this.sharedFeedbackService.openCommonFeedbackDialog({
      description,
      category: CreateFeedbackInputKategorie.EintragInNachschlagetabelle,
      table,
      value
    });
  }

  feedbackButtonIsVisible(field: SachverhaltField): boolean {
    return !(this.userHasRight && field.isOpen) && field.table !== null;
  }

  getPath(value: string): string {
    const splitted = value.split('|');
    return splitted[0];
  }

  getFileName(value: string): string {
    if (_.isNil(value)) {
      return '';
    }
    const splitted = value.split('|');
    return splitted[2];
  }
  
  loadItems = async (term: string, signal: AbortSignal): Promise<ObjektAutoCompleteResult[]> => {
    try {
      const response = await this.apiClient.apiAutoCompleteObjektGet(this.field.objektTyp, term)
        .pipe(takeUntil(abortSignalToObservable(signal))).toPromise();
      return response;
    } catch {
      return [];
    }
  };  
}
